import React, { forwardRef } from 'react'
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form'

import { cn } from '@/utils/cn'

import { Input, type InputProps } from '../Input/Input'

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  spacing?: 'none' | 'sm' | 'md' | 'lg'
}

const FIELD_SPACING = {
  none: 'space-y-0',
  sm: 'space-y-2',
  md: 'space-y-3',
  lg: 'space-y-4'
} as const

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  ({ children, className, spacing = 'md', ...props }, ref) => {
    return (
      <div ref={ref} className={cn('w-full', FIELD_SPACING[spacing], className)} {...props}>
        {children}
      </div>
    )
  }
)

FormField.displayName = 'FormField'

// React Hook Form integration component
export interface FormInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<InputProps, 'name' | 'error'> {
  control: Control<TFieldValues>
  name: TName
  description?: string
}

export function FormInputField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  description,
  className,
  ...inputProps
}: FormInputFieldProps<TFieldValues, TName>) {
  return (
    <FormField className={className}>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            {...inputProps}
            error={fieldState.error?.message}
            helperText={!fieldState.error ? description : undefined}
            aria-invalid={!!fieldState.error}
          />
        )}
      />
    </FormField>
  )
}

// Form Label Component
export interface FormLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean
  children: React.ReactNode
}

export const FormLabel = forwardRef<HTMLLabelElement, FormLabelProps>(
  ({ children, className, required = false, ...props }, ref) => {
    return (
      <label
        ref={ref}
        className={cn('block text-sm font-medium text-gray-700 mb-1 transition-colors', className)}
        {...props}
      >
        {children}
        {required && (
          <span className="text-red-500 ml-1" aria-label="obligatorio">
            *
          </span>
        )}
      </label>
    )
  }
)

FormLabel.displayName = 'FormLabel'

// Form Description Component
export interface FormDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode
}

export const FormDescription = forwardRef<HTMLParagraphElement, FormDescriptionProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <p ref={ref} className={cn('text-sm text-gray-600', className)} {...props}>
        {children}
      </p>
    )
  }
)

FormDescription.displayName = 'FormDescription'

// Form Error Message Component
export interface FormErrorMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode
}

export const FormErrorMessage = forwardRef<HTMLParagraphElement, FormErrorMessageProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn('text-sm text-red-600 flex items-center gap-1', className)}
        role="alert"
        aria-live="polite"
        {...props}
      >
        {children}
      </p>
    )
  }
)

FormErrorMessage.displayName = 'FormErrorMessage'
